import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Eye,
    Pencil,
    X,
    RefreshCw,
    Mail,
    Code2,
    Search,
    Lock,
    KeyRound,
    PartyPopper,
    ShieldAlert,
    CheckCircle2,
    XCircle,
    BadgeCheck,
    UserPlus,
    Cake,
    HeartHandshake,
    Receipt,
    CreditCard,
} from "lucide-react";
import EmailTemplatesService from "../../../services/emailTemplates.service";

const PINK = "#d66a79";
const PINK_HOVER = "#16a34a";
const BORDER = "#dbe4ef";
const BG = "#ffffff";
const TEXT = "#0f172a";
const MUTED = "#64748b";

function escapeRegExp(str = "") {
    return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function normalizeVariableToken(value = "") {
    let v = String(value || "").trim();
    if (!v) return "";

    if (v.startsWith("{{") && v.endsWith("}}")) {
        v = v.slice(2, -2).trim();
    }

    if (!/^[A-Za-z_][A-Za-z0-9_.]*$/.test(v)) return "";
    return `{{${v}}}`;
}

function stripBraces(token = "") {
    return String(token).replace(/^{{\s*/, "").replace(/\s*}}$/, "");
}


const PREDEFINED_TEMPLATE_VARIABLES = {
    registration_otp: [
        "{{adminName}}",
        "{{adminEmail}}",
        "{{otp}}",
        "{{otpValidityMinutes}}",
        "{{expiryMinutes}}",
        "{{otpValidityMinutesSuffix}}",
        "{{appName}}",
    ],
    login_otp: [
        "{{adminName}}",
        "{{otp}}",
        "{{otpValidityMinutes}}",
        "{{expiryMinutes}}",
        "{{otpValidityMinutesSuffix}}",
        "{{appName}}",
    ],
    password_reset_otp: [
        "{{adminName}}",
        "{{otp}}",
        "{{otpValidityMinutes}}",
        "{{expiryMinutes}}",
        "{{otpValidityMinutesSuffix}}",
        "{{appName}}",
    ],
    change_password_otp: [
        "{{adminName}}",
        "{{otp}}",
        "{{otpValidityMinutes}}",
        "{{expiryMinutes}}",
        "{{otpValidityMinutesSuffix}}",
        "{{appName}}",
    ],
    employee_invitation: [
        "{{name}}",
        "{{employeeName}}",
        "{{employeeEmail}}",
        "{{employeeId}}",
        "{{role}}",
        "{{department}}",
        "{{tempPassword}}",
        "{{temporaryPassword}}",
        "{{employeeBlock}}",
        "{{appName}}",
    ],
    employee_approval_approved: [
        "{{name}}",
        "{{employeeName}}",
        "{{employeeId}}",
        "{{role}}",
        "{{roleLabel}}",
        "{{status}}",
        "{{approvalStatus}}",
        "{{note}}",
        "{{reason}}",
        "{{statusLine}}",
        "{{nextStep}}",
        "{{employeeBlock}}",
        "{{noteBlock}}",
        "{{appName}}",
    ],
    employee_approval_rejected: [
        "{{name}}",
        "{{employeeName}}",
        "{{employeeId}}",
        "{{role}}",
        "{{roleLabel}}",
        "{{status}}",
        "{{approvalStatus}}",
        "{{note}}",
        "{{reason}}",
        "{{statusLine}}",
        "{{nextStep}}",
        "{{employeeBlock}}",
        "{{noteBlock}}",
        "{{appName}}",
    ],
    delivery_partner_created: [
        "{{name}}",
        "{{partnerName}}",
        "{{partnerId}}",
        "{{tempPassword}}",
        "{{temporaryPassword}}",
        "{{phone}}",
        "{{vehicleType}}",
        "{{city}}",
        "{{partnerBlock}}",
        "{{appName}}",
    ],
    delivery_partner_approved: [
        "{{name}}",
        "{{partnerName}}",
        "{{partnerId}}",
        "{{status}}",
        "{{note}}",
        "{{reason}}",
        "{{statusLine}}",
        "{{nextStep}}",
        "{{partnerBlock}}",
        "{{noteBlock}}",
        "{{appName}}",
    ],
    delivery_partner_rejected: [
        "{{name}}",
        "{{partnerName}}",
        "{{partnerId}}",
        "{{status}}",
        "{{note}}",
        "{{reason}}",
        "{{statusLine}}",
        "{{nextStep}}",
        "{{partnerBlock}}",
        "{{noteBlock}}",
        "{{appName}}",
    ],
    delivery_partner_deactivated: [
        "{{name}}",
        "{{partnerName}}",
        "{{partnerId}}",
        "{{status}}",
        "{{reason}}",
        "{{note}}",
        "{{statusLine}}",
        "{{partnerBlock}}",
        "{{reasonBlock}}",
        "{{appName}}",
    ],
    delivery_partner_reactivated: [
        "{{name}}",
        "{{partnerName}}",
        "{{partnerId}}",
        "{{status}}",
        "{{reason}}",
        "{{note}}",
        "{{statusLine}}",
        "{{partnerBlock}}",
        "{{reasonBlock}}",
        "{{appName}}",
    ],
    delivery_partner_password_changed: [
        "{{name}}",
        "{{partnerName}}",
        "{{partnerId}}",
        "{{partnerBlock}}",
        "{{appName}}",
    ],
    payment_success: ["{{name}}", "{{amount}}", "{{orderId}}", "{{appName}}"],
    payment_failed: ["{{name}}", "{{amount}}", "{{orderId}}", "{{appName}}"],
    broadcast_email: [
        "{{name}}",
        "{{title}}",
        "{{body}}",
        "{{imageBlock}}",
        "{{actionBlock}}",
        "{{appName}}",
    ],
};

function getPredefinedVariables(template = {}) {
    const key = template?.templateKey || template?.key || "";
    const predefined = PREDEFINED_TEMPLATE_VARIABLES[key];

    if (Array.isArray(predefined) && predefined.length) {
        return predefined;
    }

    if (Array.isArray(template?.variables) && template.variables.length) {
        return template.variables
            .map((token) => normalizeVariableToken(token))
            .filter(Boolean);
    }

    return [];
}

function toTitleCase(value = "") {
    return String(value)
        .replace(/[_.-]/g, " ")
        .replace(/\b\w/g, (m) => m.toUpperCase());
}

function buildSampleValue(token) {
    const key = stripBraces(token);

    const map = {
        adminName: "Wholesome",
        adminEmail: "Wholesome@example.com",
        otp: "123456",
        expiryMinutes: "10",
        otpValidityMinutes: "10",
        otpValidityMinutesSuffix: "s",
        appName: "Wholesum",
        name: "Rahul Sharma",
        employeeName: "Ajay Sharma",
        employeeEmail: "ajay@example.com",
        employeeId: "EMP-1001",
        tempPassword: "Temp@123",
        note: "Documents are incomplete",
        reason: "Documents are incomplete",
        role: "Admin Employee",
        roleLabel: "Admin Employee",
        resetLink: "https://example.com/reset-password",
        email: "user@example.com",
        warehouseName: "Central Warehouse",
        partnerName: "Ravi Kumar",
        partnerId: "DP-1001",
        phone: "9876543210",
        vehicleType: "Bike",
        city: "Delhi",
        status: "APPROVED",
        statusLine: "Your account has been approved successfully.",
        nextStep: "You can now login and access your dashboard.",
        employeeBlock: "<p><b>ID:</b> EMP-1001</p>",
        partnerBlock: "<p><b>Partner ID:</b> DP-1001</p>",
        noteBlock: "<p><b>Admin note:</b> Documents verified</p>",
        reasonBlock: "<p><b>Reason:</b> Documents are incomplete</p>",
        approvalStatus: "APPROVED",
        amount: "₹2,499",
        orderId: "#ORD-1024",
        title: "Big Fresh Offer",
        body: "Fresh groceries, daily essentials and exclusive offers are now available for you. Open Wholesum and explore the latest broadcast update.",
        imageBlock:
            '<img src="https://dummyimage.com/640x280/f8c7cf/d86a78&text=Wholesum+Broadcast" alt="Broadcast Image" style="width:100%;display:block;max-height:280px;object-fit:cover;" />',
        actionBlock:
            '<a href="#" style="display:inline-block;background:#d86a78;color:#ffffff;text-decoration:none;padding:12px 22px;border-radius:12px;font-weight:700;">View Details</a>',
    };

    return map[key] || `Sample ${toTitleCase(key)}`;
}

function renderWithSamples(content = "", variables = []) {
    let output = String(content || "");
    for (const token of variables || []) {
        const sample = buildSampleValue(token);
        output = output.replace(new RegExp(escapeRegExp(token), "g"), sample);
    }
    return output;
}

function getFallbackDefaultTemplate(template) {
    const key = template?.templateKey;

    const defaults = {
        registration_otp: {
            subject: "🔐 Verify Your Email - OTP: {{otp}}",
            htmlContent: `
<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8" />
<title>Registration OTP</title>
<style>
  body { font-family: Arial, sans-serif; background: #f9fafb; margin: 0; padding: 20px; color: #334155; }
  .container { max-width: 620px; margin: auto; background: #ffffff; border-radius: 14px; overflow: hidden; box-shadow: 0 8px 24px rgba(15,23,42,0.08); }
  .header { background: linear-gradient(135deg, #667eea, #764ba2); color: #fff; text-align: center; padding: 42px 20px; }
  .icon { font-size: 42px; margin-bottom: 12px; }
  .content { padding: 28px; line-height: 1.7; font-size: 15px; }
  .otp { font-size: 28px; font-weight: 700; letter-spacing: 6px; background: #f8fafc; border: 1px dashed #cbd5e1; border-radius: 12px; text-align: center; padding: 16px; margin: 20px 0; color: #111827; }
  .footer { padding: 18px 28px 28px; color: #64748b; font-size: 13px; }
</style>
</head>
<body>
  <div class="container">
    <div class="header">
      <div class="icon">🔐</div>
      <h1 style="margin:0;font-size:34px;">Verify Your Email Address</h1>
      <p style="margin:12px 0 0;font-size:18px;opacity:0.95;">Complete your registration</p>
    </div>
    <div class="content">
      <p>Hi {{adminName}},</p>
      <p>Thank you for registering with <b>Admin Panel</b>! To complete your registration and activate your account, please verify your email address using the One-Time Password (OTP) below:</p>
      <div class="otp">{{otp}}</div>
      <p>This OTP will expire in <b>{{expiryMinutes}} minutes</b>.</p>
      <p>If you did not create this account, please ignore this email.</p>
    </div>
    <div class="footer">
      Regards,<br />
      Admin Panel Team
    </div>
  </div>
</body>
</html>`,
            variables: ["{{adminName}}", "{{adminEmail}}", "{{otp}}", "{{expiryMinutes}}"],
        },

        login_otp: {
            subject: "🔑 Login Verification - OTP: {{otp}}",
            htmlContent: `<h2>Login Verification Required</h2><p>Hi {{adminName}},</p><p>Your OTP is <b>{{otp}}</b></p>`,
            variables: ["{{adminName}}", "{{otp}}"],
        },

        employee_invitation: {
            subject: "🎉 Welcome - Your Account is Created",
            htmlContent: `<p>Hello {{name}},</p><p>Your account has been created.</p><p><b>EMP ID:</b> {{employeeId}}</p><p><b>Password:</b> {{tempPassword}}</p>`,
            variables: ["{{name}}", "{{employeeId}}", "{{tempPassword}}"],
        },

        password_reset_otp: {
            subject: "🔐 Reset Password OTP: {{otp}}",
            htmlContent: `<p>Hi {{adminName}},</p><p>Your reset OTP is <b>{{otp}}</b></p>`,
            variables: ["{{adminName}}", "{{otp}}"],
        },

        payment_success: {
            subject: "✅ Payment Success - Thank You",
            htmlContent: `<p>Hello {{name}},</p><p>Your payment of <b>{{amount}}</b> was successful.</p><p>Order ID: {{orderId}}</p>`,
            variables: ["{{name}}", "{{amount}}", "{{orderId}}"],
        },

        payment_failed: {
            subject: "❌ Payment Failed",
            htmlContent: `<p>Hello {{name}},</p><p>Your payment for <b>{{orderId}}</b> could not be processed.</p>`,
            variables: ["{{name}}", "{{orderId}}"],
        },

        delivery_partner_created: {
            subject: "🎉 Your Delivery Partner Account Has Been Created",
            htmlContent: `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8" />
  <title>Delivery Partner Account Created</title>
</head>
<body style="margin:0;padding:20px;background:#f8fafc;font-family:Arial,sans-serif;color:#334155;">
  <div style="max-width:620px;margin:auto;background:#ffffff;border-radius:16px;overflow:hidden;box-shadow:0 8px 24px rgba(15,23,42,0.08);">
    <div style="background:linear-gradient(135deg,#16a34a,#22c55e);color:#fff;text-align:center;padding:38px 20px;">
      <div style="font-size:40px;margin-bottom:10px;">🎉</div>
      <h1 style="margin:0;font-size:30px;">Account Created</h1>
      <p style="margin:10px 0 0;font-size:16px;opacity:.95;">Your delivery partner account is ready</p>
    </div>
    <div style="padding:28px;line-height:1.7;font-size:15px;">
      <p>Hello {{partnerName}},</p>
      <p>Your delivery partner account has been created successfully.</p>
      <p><b>Partner ID:</b> {{partnerId}}</p>
      <p><b>Temporary Password:</b> {{tempPassword}}</p>
      <p>Please login and complete your password setup.</p>
    </div>
    <div style="padding:18px 28px 28px;color:#64748b;font-size:13px;">
      Regards,<br/>Wholesum Team
    </div>
  </div>
</body>
</html>`,
            variables: ["{{partnerName}}", "{{partnerId}}", "{{tempPassword}}"],
        },

        delivery_partner_approved: {
            subject: "✅ Application Approved",
            htmlContent: `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8" />
  <title>Delivery Partner Approved</title>
</head>
<body style="margin:0;padding:20px;background:#f8fafc;font-family:Arial,sans-serif;color:#334155;">
  <div style="max-width:620px;margin:auto;background:#ffffff;border-radius:16px;overflow:hidden;box-shadow:0 8px 24px rgba(15,23,42,0.08);">
    <div style="background:linear-gradient(135deg,#16a34a,#22c55e);color:#fff;text-align:center;padding:38px 20px;">
      <div style="font-size:40px;margin-bottom:10px;">✅</div>
      <h1 style="margin:0;font-size:30px;">Delivery Partner Approved</h1>
      <p style="margin:10px 0 0;font-size:16px;opacity:.95;">Your account is ready to use</p>
    </div>
    <div style="padding:28px;line-height:1.7;font-size:15px;">
      <p>Hi {{partnerName}},</p>
      <p>Your delivery partner onboarding request has been approved.</p>
      <p><b>Partner ID:</b> {{partnerId}}</p>
      <p>You can now login and access your dashboard.</p>
    </div>
    <div style="padding:18px 28px 28px;color:#64748b;font-size:13px;">
      Regards,<br/>Wholesum Team
    </div>
  </div>
</body>
</html>`,
            variables: ["{{partnerName}}", "{{partnerId}}"],
        },

        delivery_partner_rejected: {
            subject: "❌ Application Rejected",
            htmlContent: `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8" />
  <title>Delivery Partner Rejected</title>
</head>
<body style="margin:0;padding:20px;background:#f8fafc;font-family:Arial,sans-serif;color:#334155;">
  <div style="max-width:620px;margin:auto;background:#ffffff;border-radius:16px;overflow:hidden;box-shadow:0 8px 24px rgba(15,23,42,0.08);">
    <div style="background:linear-gradient(135deg,#dc2626,#ef4444);color:#fff;text-align:center;padding:38px 20px;">
      <div style="font-size:40px;margin-bottom:10px;">❌</div>
      <h1 style="margin:0;font-size:30px;">Application Rejected</h1>
      <p style="margin:10px 0 0;font-size:16px;opacity:.95;">Please review and update your details</p>
    </div>
    <div style="padding:28px;line-height:1.7;font-size:15px;">
      <p>Hi {{partnerName}},</p>
      <p>Your delivery partner onboarding request has been rejected.</p>
      <p><b>Partner ID:</b> {{partnerId}}</p>
      <p><b>Reason:</b> {{reason}}</p>
    </div>
    <div style="padding:18px 28px 28px;color:#64748b;font-size:13px;">
      Regards,<br/>Wholesum Team
    </div>
  </div>
</body>
</html>`,
            variables: ["{{partnerName}}", "{{partnerId}}", "{{reason}}"],
        },

        delivery_partner_deactivated: {
            subject: "⚠️ Your Delivery Partner Account Has Been Deactivated",
            htmlContent: `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8" />
  <title>Delivery Partner Deactivated</title>
</head>
<body style="margin:0;padding:20px;background:#f8fafc;font-family:Arial,sans-serif;color:#334155;">
  <div style="max-width:620px;margin:auto;background:#ffffff;border-radius:16px;overflow:hidden;box-shadow:0 8px 24px rgba(15,23,42,0.08);">
    <div style="background:linear-gradient(135deg,#f59e0b,#f97316);color:#fff;text-align:center;padding:38px 20px;">
      <div style="font-size:40px;margin-bottom:10px;">⚠️</div>
      <h1 style="margin:0;font-size:30px;">Account Deactivated</h1>
      <p style="margin:10px 0 0;font-size:16px;opacity:.95;">Your account has been disabled by admin</p>
    </div>
    <div style="padding:28px;line-height:1.7;font-size:15px;">
      <p>Hello {{partnerName}},</p>
      <p>Your delivery partner account has been deactivated by admin.</p>
      <p><b>Partner ID:</b> {{partnerId}}</p>
      <p><b>Reason:</b> {{reason}}</p>
    </div>
    <div style="padding:18px 28px 28px;color:#64748b;font-size:13px;">
      Regards,<br/>Wholesum Team
    </div>
  </div>
</body>
</html>`,
            variables: ["{{partnerName}}", "{{partnerId}}", "{{reason}}"],
        },

        delivery_partner_reactivated: {
            subject: "✅ Your Delivery Partner Account Has Been Activated",
            htmlContent: `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8" />
  <title>Delivery Partner Activated</title>
</head>
<body style="margin:0;padding:20px;background:#f8fafc;font-family:Arial,sans-serif;color:#334155;">
  <div style="max-width:620px;margin:auto;background:#ffffff;border-radius:16px;overflow:hidden;box-shadow:0 8px 24px rgba(15,23,42,0.08);">
    <div style="background:linear-gradient(135deg,#16a34a,#22c55e);color:#fff;text-align:center;padding:38px 20px;">
      <div style="font-size:40px;margin-bottom:10px;">✅</div>
      <h1 style="margin:0;font-size:30px;">Account Activated</h1>
      <p style="margin:10px 0 0;font-size:16px;opacity:.95;">Your account has been activated by admin</p>
    </div>
    <div style="padding:28px;line-height:1.7;font-size:15px;">
      <p>Hello {{partnerName}},</p>
      <p>Your delivery partner account has been activated by admin.</p>
      <p><b>Partner ID:</b> {{partnerId}}</p>
    </div>
    <div style="padding:18px 28px 28px;color:#64748b;font-size:13px;">
      Regards,<br/>Wholesum Team
    </div>
  </div>
</body>
</html>`,
            variables: ["{{partnerName}}", "{{partnerId}}"],
        },

        delivery_partner_password_changed: {
            subject: "🔐 Your Password Was Changed Successfully",
            htmlContent: `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8" />
  <title>Password Changed</title>
</head>
<body style="margin:0;padding:20px;background:#f8fafc;font-family:Arial,sans-serif;color:#334155;">
  <div style="max-width:620px;margin:auto;background:#ffffff;border-radius:16px;overflow:hidden;box-shadow:0 8px 24px rgba(15,23,42,0.08);">
    <div style="background:linear-gradient(135deg,#2563eb,#3b82f6);color:#fff;text-align:center;padding:38px 20px;">
      <div style="font-size:40px;margin-bottom:10px;">🔐</div>
      <h1 style="margin:0;font-size:30px;">Password Changed</h1>
      <p style="margin:10px 0 0;font-size:16px;opacity:.95;">Your password was updated successfully</p>
    </div>
    <div style="padding:28px;line-height:1.7;font-size:15px;">
      <p>Hello {{partnerName}},</p>
      <p>Your password has been changed successfully.</p>
      <p><b>Partner ID:</b> {{partnerId}}</p>
      <p>If this was not done by you, please contact support immediately.</p>
    </div>
    <div style="padding:18px 28px 28px;color:#64748b;font-size:13px;">
      Regards,<br/>Wholesum Team
    </div>
  </div>
</body>
</html>`,
            variables: ["{{partnerName}}", "{{partnerId}}"],
        },
    };

    return (
        defaults[key] || {
            subject: template?.subject || "",
            htmlContent: template?.htmlContent || "",
            variables: Array.isArray(template?.variables)
                ? template.variables.map((token) => normalizeVariableToken(token)).filter(Boolean)
                : [],
        }
    );
}

function getTemplateIcon(key = "") {
    const k = String(key || "").toLowerCase();

    if (k.includes("registration")) return Lock;
    if (k.includes("login")) return KeyRound;
    if (k.includes("employee") && k.includes("invitation")) return PartyPopper;
    if (k.includes("failed_login")) return ShieldAlert;
    if (k.includes("password_reset_otp")) return KeyRound;
    if (k.includes("password_reset_request")) return Mail;
    if (k.includes("password_changed")) return BadgeCheck;
    if (k.includes("welcome")) return UserPlus;
    if (k.includes("birthday")) return Cake;
    if (k.includes("anniversary")) return HeartHandshake;
    if (k.includes("payment_success")) return CheckCircle2;
    if (k.includes("payment_failed")) return XCircle;
    if (k.includes("delivery_partner_created")) return UserPlus;
    if (k.includes("delivery_partner_approved")) return CheckCircle2;
    if (k.includes("delivery_partner_rejected")) return XCircle;
    if (k.includes("delivery_partner_deactivated")) return ShieldAlert;
    if (k.includes("delivery_partner_reactivated")) return BadgeCheck;
    if (k.includes("delivery_partner_password_changed")) return KeyRound;

    return Mail;
}

function Card({ children, className = "" }) {
    return (
        <div
            className={`rounded-[20px] bg-white ${className}`}
            style={{
                border: `1px solid ${BORDER}`,
                boxShadow: "0 8px 28px rgba(15,23,42,0.04)",
            }}
        >
            {children}
        </div>
    );
}

function ModalShell({ children, onClose, widthClass = "max-w-4xl" }) {
    return (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-950/40 p-4">
            <div
                className={`relative max-h-[92vh] w-full overflow-hidden rounded-[24px] bg-white shadow-2xl ${widthClass}`}
            >
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full border bg-white text-slate-500 transition hover:bg-slate-50"
                    style={{ borderColor: BORDER }}
                >
                    <X size={18} />
                </button>
                {children}
            </div>
        </div>
    );
}

export default function EmailTemplates() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [search, setSearch] = useState("");

    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [isEditOpen, setIsEditOpen] = useState(false);

    const [form, setForm] = useState({
        templateKey: "",
        name: "",
        subject: "",
        htmlContent: "",
        variables: [],
    });

    const subjectRef = useRef(null);
    const htmlRef = useRef(null);
    const [activeField, setActiveField] = useState("htmlContent");
    const savedSnapshotRef = useRef(null);

    async function loadTemplates() {
        setLoading(true);
        try {
            const rows = await EmailTemplatesService.listTemplates();
            const all = Array.isArray(rows) ? rows : [];
            setTemplates(all);

            if (all.length && !selectedTemplate) {
                const first = all[0];
                setSelectedTemplate(first);
            }
        } catch (error) {
            console.error("Failed to load email templates:", error);
            setTemplates([]);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        loadTemplates();
    }, []);

    const filteredTemplates = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return templates;

        return templates.filter((item) => {
            const hay = [
                item?.name,
                item?.templateKey,
                item?.subject,
                item?.description,
                ...(Array.isArray(item?.variables) ? item.variables : []),
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();

            return hay.includes(q);
        });
    }, [templates, search]);

    useEffect(() => {
        if (!selectedTemplate && filteredTemplates.length) {
            setSelectedTemplate(filteredTemplates[0]);
            return;
        }

        if (
            selectedTemplate &&
            !filteredTemplates.some(
                (t) => (t.id || t.templateKey) === (selectedTemplate.id || selectedTemplate.templateKey)
            )
        ) {
            setSelectedTemplate(filteredTemplates[0] || null);
        }
    }, [filteredTemplates, selectedTemplate]);

    const selectedTemplatePreview = useMemo(() => {
        if (!selectedTemplate) return null;

        return {
            ...selectedTemplate,
            renderedSubject: renderWithSamples(
                selectedTemplate.subject || "",
                getPredefinedVariables(selectedTemplate)
            ),
            renderedHtml: renderWithSamples(
                selectedTemplate.htmlContent || "",
                getPredefinedVariables(selectedTemplate)
            ),
        };
    }, [selectedTemplate]);

    const previewSubject = useMemo(
        () => renderWithSamples(form.subject, form.variables),
        [form.subject, form.variables]
    );

    const previewHtml = useMemo(
        () => renderWithSamples(form.htmlContent, form.variables),
        [form.htmlContent, form.variables]
    );

    function updateForm(field, value) {
        setForm((prev) => ({ ...prev, [field]: value }));
    }

    function openEditor(template) {
        const row = {
            templateKey: template?.templateKey || "",
            name: template?.name || "",
            subject: template?.subject || "",
            htmlContent: template?.htmlContent || "",
            variables: getPredefinedVariables(template),
        };

        savedSnapshotRef.current = row;
        setForm(row);
        setIsEditOpen(true);
    }

    async function handleEdit(templateKey) {
        try {
            const row = await EmailTemplatesService.getTemplate(templateKey);
            if (!row) return;
            setSelectedTemplate(row);
            openEditor(row);
        } catch (error) {
            console.error("Failed to fetch template:", error);
        }
    }

    function insertTokenIntoActiveField(token) {
        const targetRef = activeField === "subject" ? subjectRef : htmlRef;
        const setterField = activeField === "subject" ? "subject" : "htmlContent";
        const el = targetRef.current;

        if (!el) {
            updateForm(setterField, `${form[setterField] || ""}${token}`);
            return;
        }

        const start = el.selectionStart ?? el.value.length;
        const end = el.selectionEnd ?? el.value.length;
        const current = form[setterField] || "";
        const next = current.slice(0, start) + token + current.slice(end);

        updateForm(setterField, next);

        requestAnimationFrame(() => {
            el.focus();
            const pos = start + token.length;
            el.setSelectionRange(pos, pos);
        });
    }

    function loadDefaultTemplate() {
        const defaults = getFallbackDefaultTemplate(
            savedSnapshotRef.current || selectedTemplate || form
        );

        setForm((prev) => ({
            ...prev,
            subject: defaults.subject || "",
            htmlContent: defaults.htmlContent || "",
            variables: Array.isArray(defaults.variables) ? defaults.variables : [],
        }));
    }

    async function saveChanges() {
        if (!form.templateKey) return;

        setSaving(true);
        try {
            await EmailTemplatesService.updateTemplate(form.templateKey, {
                name: form.name,
                subject: form.subject,
                htmlContent: form.htmlContent,
                variables: getPredefinedVariables(form),
            });

            await loadTemplates();

            const fresh = await EmailTemplatesService.getTemplate(form.templateKey);
            if (fresh) {
                setSelectedTemplate(fresh);
            }

            setIsEditOpen(false);
        } catch (error) {
            console.error("Failed to save template:", error);
        } finally {
            setSaving(false);
        }
    }

    const SelectedIcon = getTemplateIcon(selectedTemplate?.templateKey);

    return (
        <div className="min-h-screen p-4 md:p-6" style={{ background: BG }}>
            <div className="mx-auto max-w-[1500px]">
                <div className="grid grid-cols-1 gap-5 xl:grid-cols-[360px_minmax(0,1fr)]">
                    <Card className="overflow-hidden">
                        <div className="border-b px-5 py-4" style={{ borderColor: BORDER }}>
                            <div className="flex items-center justify-between gap-3">
                                <div>
                                    <div className="text-[12px] font-bold uppercase tracking-[0.18em] text-slate-400">
                                        Template Types
                                    </div>
                                </div>

                                {/* <button
                                    type="button"
                                    className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-[13px] font-semibold text-white"
                                    style={{ backgroundColor: PINK }}
                                >
                                    <Plus size={14} />
                                    Custom
                                </button> */}
                            </div>
                        </div>

                        <div className="p-4">
                            <div className="relative mb-4">
                                <Search
                                    size={16}
                                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                                />
                                <input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Search templates..."
                                    className="w-full rounded-[14px] border bg-white py-2.5 pl-10 pr-4 text-[14px] outline-none"
                                    style={{ borderColor: BORDER }}
                                />
                            </div>

                            <div className="max-h-[72vh] space-y-2 overflow-y-auto pr-1">
                                {loading ? (
                                    <div className="py-8 text-center text-[14px] text-slate-500">
                                        Loading templates...
                                    </div>
                                ) : filteredTemplates.length === 0 ? (
                                    <div className="py-8 text-center text-[14px] text-slate-500">
                                        No templates found.
                                    </div>
                                ) : (
                                    filteredTemplates.map((item) => {
                                        const Icon = getTemplateIcon(item.templateKey);
                                        const active =
                                            (selectedTemplate?.id || selectedTemplate?.templateKey) ===
                                            (item.id || item.templateKey);

                                        return (
                                            <button
                                                key={item.id || item.templateKey}
                                                type="button"
                                                onClick={() => setSelectedTemplate(item)}
                                                className="flex w-full items-center gap-3 rounded-[14px] border px-4 py-3 text-left transition"
                                                style={{
                                                    borderColor: active ? "#bfd4ff" : "transparent",
                                                    background: active ? "#eff6ff" : "transparent",
                                                }}
                                            >
                                                <Icon
                                                    size={16}
                                                    style={{ color: active ? "#2563eb" : "#f59e0b" }}
                                                />
                                                <span
                                                    className="min-w-0 truncate text-[15px] font-medium"
                                                    style={{ color: active ? "#2563eb" : TEXT }}
                                                >
                                                    {item.name || item.templateKey}
                                                </span>
                                            </button>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    </Card>

                    <Card className="overflow-hidden">
                        {!selectedTemplatePreview ? (
                            <div className="flex min-h-[600px] items-center justify-center text-[14px] text-slate-500">
                                Select a template to preview.
                            </div>
                        ) : (
                            <>
                                <div
                                    className="flex items-start justify-between gap-4 border-b px-6 py-5"
                                    style={{ borderColor: BORDER }}
                                >
                                    <div>
                                        <div className="flex items-center gap-3">
                                            <div
                                                className="flex h-10 w-10 items-center justify-center rounded-xl"
                                                style={{ background: "#eef2ff", color: "#4f46e5" }}
                                            >
                                                <SelectedIcon size={18} />
                                            </div>
                                            <div className="text-[18px] font-bold text-slate-900">
                                                {selectedTemplatePreview.name || selectedTemplatePreview.templateKey}
                                            </div>
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={() => handleEdit(selectedTemplatePreview.templateKey)}
                                        className="inline-flex items-center gap-2 rounded-[12px] px-4 py-2.5 text-[14px] font-semibold text-white"
                                        style={{ backgroundColor: PINK }}
                                    >
                                        <Pencil size={15} />
                                        Edit
                                    </button>
                                </div>

                                <div className="p-5">
                                    <div
                                        className="mb-4 rounded-[14px] border bg-white"
                                        style={{ borderColor: BORDER }}
                                    >
                                        <div
                                            className="border-b px-4 py-3 text-[12px] font-bold uppercase tracking-[0.14em] text-slate-400"
                                            style={{ borderColor: BORDER }}
                                        >
                                            Subject
                                        </div>
                                        <div className="px-4 py-4 text-[15px] text-slate-800">
                                            {selectedTemplatePreview.renderedSubject || "-"}
                                        </div>
                                    </div>

                                    <div
                                        className="rounded-[14px] border bg-white"
                                        style={{ borderColor: BORDER }}
                                    >
                                        <div
                                            className="border-b px-4 py-3 text-[12px] font-bold uppercase tracking-[0.14em] text-slate-400"
                                            style={{ borderColor: BORDER }}
                                        >
                                            Preview
                                        </div>

                                        <div className="max-h-[68vh] overflow-auto bg-[#f8fafc] p-4">
                                            <div
                                                className="mx-auto min-h-[420px] max-w-[860px] rounded-[12px] bg-white p-4"
                                                style={{ boxShadow: "inset 0 0 0 1px #e2e8f0" }}
                                            >
                                                <div
                                                    dangerouslySetInnerHTML={{
                                                        __html:
                                                            selectedTemplatePreview.renderedHtml ||
                                                            "<p style='color:#94a3b8'>No preview available.</p>",
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </Card>
                </div>
            </div>

            {isEditOpen ? (
                <ModalShell onClose={() => setIsEditOpen(false)} widthClass="max-w-5xl">
                    <div className="flex max-h-[92vh] flex-col">
                        <div className="border-b px-6 py-5" style={{ borderColor: BORDER }}>
                            <div className="pr-10">
                                <div className="flex items-start gap-3">
                                    <div
                                        className="flex h-10 w-10 items-center justify-center rounded-xl"
                                        style={{ background: "#fff7ed", color: "#f97316" }}
                                    >
                                        <Pencil size={18} />
                                    </div>
                                    <div>
                                        <h2 className="text-[20px] font-bold text-slate-900">
                                            Edit Template
                                        </h2>
                                        <p className="mt-1 text-[13px] text-slate-500">
                                            Customize email content, variables and status.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="overflow-y-auto px-6 py-5">
                            <div className="grid grid-cols-1 gap-5">
                                <div>
                                    <label className="mb-2 block text-[13px] font-semibold text-slate-700">
                                        Template Name <span className="text-rose-500">*</span>
                                    </label>
                                    <input
                                        value={form.name}
                                        onChange={(e) => updateForm("name", e.target.value)}
                                        className="w-full rounded-[14px] border px-4 py-3 text-[14px] outline-none"
                                        style={{ borderColor: BORDER }}
                                    />
                                </div>

                                <div>
                                    <label className="mb-2 block text-[13px] font-semibold text-slate-700">
                                        Email Subject <span className="text-rose-500">*</span>
                                    </label>
                                    <input
                                        ref={subjectRef}
                                        value={form.subject}
                                        onFocus={() => setActiveField("subject")}
                                        onChange={(e) => updateForm("subject", e.target.value)}
                                        className="w-full rounded-[14px] border px-4 py-3 text-[14px] outline-none"
                                        style={{ borderColor: BORDER }}
                                    />
                                </div>

                                <div
                                    className="flex items-center justify-between rounded-[14px] border bg-slate-50 px-4 py-4"
                                    style={{ borderColor: BORDER }}
                                >
                                    <div>
                                        <div className="text-[14px] font-semibold text-slate-700">
                                            System Template
                                        </div>
                                        <div className="text-[13px] text-slate-500">
                                            Load default HTML & variables for this template type.
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={loadDefaultTemplate}
                                        className="rounded-[12px] border bg-white px-4 py-2.5 text-[14px] font-semibold text-blue-600"
                                        style={{ borderColor: "#bfdbfe" }}
                                    >
                                        Load Default
                                    </button>
                                </div>

                                <div>
                                    <div className="mb-3 flex items-center justify-between gap-3">
                                        <div>
                                            <div className="text-[14px] font-semibold text-slate-700">
                                                Available Variables
                                            </div>
                                            <div className="mt-1 text-[12px] text-slate-500">
                                                These variables are fixed by backend. Click any chip to insert it into the active subject/content field.
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex flex-wrap gap-2">
                                        {(form.variables || []).length ? (
                                            form.variables.map((token) => (
                                                <button
                                                    key={token}
                                                    type="button"
                                                    onClick={() => insertTokenIntoActiveField(token)}
                                                    className="rounded-full border bg-white px-3 py-2 text-[12px] font-semibold text-slate-700 transition hover:bg-slate-50"
                                                    style={{ borderColor: BORDER }}
                                                    title="Click to insert"
                                                >
                                                    {token}
                                                </button>
                                            ))
                                        ) : (
                                            <div className="text-[13px] text-slate-400">
                                                No predefined variables for this template.
                                            </div>
                                        )}
                                    </div>

                                    <div className="mt-2 text-[12px] text-slate-500">
                                        Random variables are disabled to prevent broken emails like <b>{"{{wrongVariable}}"}</b>.
                                    </div>
                                </div>

                                <div>
                                    <label className="mb-2 block text-[13px] font-semibold text-slate-700">
                                        Email Content (HTML) <span className="text-rose-500">*</span>
                                    </label>
                                    <textarea
                                        ref={htmlRef}
                                        rows={18}
                                        value={form.htmlContent}
                                        onFocus={() => setActiveField("htmlContent")}
                                        onChange={(e) => updateForm("htmlContent", e.target.value)}
                                        className="min-h-[320px] w-full rounded-[14px] border px-4 py-3 font-mono text-[13px] leading-6 outline-none"
                                        style={{ borderColor: BORDER }}
                                    />
                                </div>

                                <div>
                                    <div className="mb-2 text-[13px] font-semibold text-slate-700">
                                        Live Preview
                                    </div>
                                    <div
                                        className="rounded-[14px] border bg-[#f8fafc] p-4"
                                        style={{ borderColor: BORDER }}
                                    >
                                        <div className="mb-3 rounded-[12px] bg-white px-4 py-3 text-[14px] font-semibold text-slate-700">
                                            {previewSubject || "-"}
                                        </div>

                                        <div className="max-h-[360px] overflow-auto rounded-[12px] bg-white p-4">
                                            <div
                                                dangerouslySetInnerHTML={{
                                                    __html:
                                                        previewHtml ||
                                                        "<p style='color:#94a3b8'>Preview will appear here.</p>",
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="border-t bg-white px-6 py-4" style={{ borderColor: BORDER }}>
                            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end">
                                <button
                                    type="button"
                                    onClick={() => setIsEditOpen(false)}
                                    className="rounded-[12px] border px-5 py-3 text-[14px] font-semibold text-slate-600"
                                    style={{ borderColor: BORDER }}
                                >
                                    Cancel
                                </button>

                                <button
                                    type="button"
                                    onClick={saveChanges}
                                    disabled={saving}
                                    className="rounded-[12px] px-5 py-3 text-[14px] font-semibold text-white disabled:opacity-60"
                                    style={{ backgroundColor: PINK }}
                                >
                                    {saving ? "Saving..." : "Save Changes"}
                                </button>
                            </div>
                        </div>
                    </div>
                </ModalShell>
            ) : null}
        </div>
    );
}